import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { SortSelect, sortData } from '../components/SortSelect';
import { FilterPanel } from '../components/FilterPanel';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ExportDialog } from '../components/ExportDialog';
import { useExport } from '../hooks/useExport';
import { exportTableToPDF } from '../utils/html2pdfExport';
import * as XLSX from 'xlsx';

import { ODENEK_EXPORT_COLUMNS, INITIAL_ODENEK_FORM_DATA, INITIAL_ODENEK_FILTERS } from '../components/modules/odenek/constants';
import { OdenekFormDialog } from '../components/modules/odenek/OdenekFormDialog';
import { FinansOdenekDetailDialog } from '../components/modules/odenek/FinansOdenekDetailDialog';
import { exportFinansOdenekDetayPDF } from '../utils/finansDetayPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OdenekPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [odenekler, setOdenekler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [sortKey, setSortKey] = useState('newest');
  const [submitting, setSubmitting] = useState(false);
  const [selectedOdenek, setSelectedOdenek] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedEkRows, setSelectedEkRows] = useState([]);
  const { selectedColumns, setSelectedColumns, pdfPageSize, setPdfPageSize, toggleColumn, selectAll, clearAll } = useExport();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [formData, setFormData] = useState({...INITIAL_ODENEK_FORM_DATA});
  const [filters, setFilters] = useState({...INITIAL_ODENEK_FILTERS});
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [detailOdenek, setDetailOdenek] = useState(null);
  const [hakedisler, setHakedisler] = useState([]);

  useEffect(() => {
    if (!user?.is_admin && !user?.can_access_finance_module && !user?.is_viewer) {
      navigate('/dashboard');
      return;
    }
    fetchOdenekler();
    fetchHakedisler();
  }, []);

  const fetchHakedisler = async () => {
    try {
      const response = await axios.get(`${API}/hakedis`, { params: { _t: Date.now() }, withCredentials: true });
      setHakedisler(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Hakedişler fetch error:', error);
    }
  };

  const fetchOdenekler = async () => {
    try {
      const response = await axios.get(`${API}/odenek`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        withCredentials: true,
      });
      setOdenekler(response.data);
    } catch (error) {
      console.error('Ödenekler fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const displayMoney = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/[^0-9,]/g, '');
    if (!cleaned) return '';
    const parts = cleaned.split(',');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const result = decimalPart !== undefined ? `${formatted},${decimalPart.substring(0, 2)}` : formatted;
    return result ? result + ' TL' : '';
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editMode) {
        await axios.put(`${API}/odenek/${selectedOdenek.id}`, formData, { withCredentials: true });
        alert('Ödenek güncellendi!');
      } else {
        await axios.post(`${API}/odenek`, formData, { withCredentials: true });
        alert('Ödenek oluşturuldu!');
      }
      setDialogOpen(false);
      resetForm();
      await fetchOdenekler();
    } catch (error) {
      console.error('Submit error:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ödeneği silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/odenek/${id}`, { withCredentials: true });
      alert('✅ Ödenek silindi!');
      await fetchOdenekler();
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Hata: ' + error.message);
    }
  };

  // Ek Taahhüt/Protokol yönetimi
  const addEkTaahhut = () => {
    setFormData({
      ...formData,
      ek_taahhutler: [...(formData.ek_taahhutler || []), { talep_yeri: '', tarih: '', yazisi: '', tutari: '', durumu: '' }]
    });
  };

  const removeEkTaahhut = (index) => {
    const newList = (formData.ek_taahhutler || []).filter((_, i) => i !== index);
    setFormData({ ...formData, ek_taahhutler: newList });
  };

  const updateEkTaahhut = (index, field, value) => {
    const newList = [...(formData.ek_taahhutler || [])];
    newList[index] = { ...newList[index], [field]: value };
    setFormData({ ...formData, ek_taahhutler: newList });
  };

  const addEkProtokol = () => {
    setFormData({
      ...formData,
      ek_protokoller: [...(formData.ek_protokoller || []), { talep_yeri: '', tarih: '', yazisi: '', tutari: '', durumu: '' }]
    });
  };

  const removeEkProtokol = (index) => {
    const newList = (formData.ek_protokoller || []).filter((_, i) => i !== index);
    setFormData({ ...formData, ek_protokoller: newList });
  };

  const updateEkProtokol = (index, field, value) => {
    const newList = [...(formData.ek_protokoller || [])];
    newList[index] = { ...newList[index], [field]: value };
    setFormData({ ...formData, ek_protokoller: newList });
  };

  const resetForm = () => {
    setFormData({...INITIAL_ODENEK_FORM_DATA});
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setDialogOpen(true);
  };

  const openEditDialog = (odenek) => {
    setSelectedOdenek(odenek);
    // Migration: Eski veriler yeni formata dönüştürülür
    const formDataToSet = { ...odenek };
    
    // Eğer yeni list yapısı yoksa boş array ata
    if (!formDataToSet.ek_taahhutler) formDataToSet.ek_taahhutler = [];
    if (!formDataToSet.ek_protokoller) formDataToSet.ek_protokoller = [];
    
    // Eski field'lar doluysa ve list boşsa, migration yap
    if (odenek.ek_taahhut_talep_tutari && formDataToSet.ek_taahhutler.length === 0) {
      formDataToSet.ek_taahhutler = [{
        tarih: odenek.ek_taahhut_talep_tarihi || '',
        yazisi: odenek.ek_taahhut_talep_yazisi || '',
        tutari: odenek.ek_taahhut_talep_tutari || '',
        durumu: odenek.ek_taahhut_odenek_durumu || ''
      }];
    }
    
    if (odenek.ek_protokol_talep_tutari && formDataToSet.ek_protokoller.length === 0) {
      formDataToSet.ek_protokoller = [{
        tarih: odenek.ek_protokol_talep_tarihi || '',
        yazisi: odenek.ek_protokol_talep_yazisi || '',
        tutari: odenek.ek_protokol_talep_tutari || '',
        durumu: odenek.ek_protokol_odenek_durumu || ''
      }];
    }
    
    // Yeni alanlar yoksa varsayılan değer ata
    if (!formDataToSet.ihale_onayi) formDataToSet.ihale_onayi = '';
    if (!formDataToSet.ihale_onayi_talep_tarihi) formDataToSet.ihale_onayi_talep_tarihi = '';
    if (!formDataToSet.ihale_onayi_talep_yazisi) formDataToSet.ihale_onayi_talep_yazisi = '';
    
    setFormData(formDataToSet);
    setEditMode(true);
    setDialogOpen(true);
  };

  const exportPDF = async () => {
    if (selectedColumns.length === 0) {
      alert('Lütfen en az bir sütun seçin');
      return;
    }

    let dataToExport;
    if (selectedRows.length > 0) {
      // Seçili satırlar varsa SADECE seçilileri al (filtrelenmiş olan seçililer)
      dataToExport = filteredOdenekler.filter(o => selectedRows.includes(o.id));
    } else {
      // Hiç seçili yoksa filtrelenmiş tümünü al
      dataToExport = filteredOdenekler;
    }

    if (dataToExport.length === 0) { alert('Dışa aktarılacak ödenek yok'); return; }

    try {
      const columnMapping = {
        'yil': 'Yil', 'isin_adi': 'Is Adi', 'talep_yeri': 'Talep Yeri',
        'odenek_turu': 'Tur', 'talep_tarihi': 'Tarih', 'talep_yazisi_sayi_no': 'Yazi No',
        'talep_tutari': 'Tutar', 'odenek_durumu': 'Durum',
        'ihale_onayi': 'Ihale Onayi', 'ihale_onayi_talep_tarihi': 'Ihale Onayi Tarih',
        'ihale_onayi_talep_yazisi': 'Ihale Onayi Yazi',
        'ek_taahhut_talep_tarihi': 'Ek Taahhut Tarih',
        'ek_protokol_talep_tarihi': 'Ek Protokol Tarih'
      };

      const headers = ['S.NO', ...selectedColumns.map(col => columnMapping[col] || col)];
      
      // Her ödenek için satırlar oluştur (ana + ek)
      let allDataRows = [];
      let rowIndex = 1;
      dataToExport.forEach(od => {
        const visibility = getSatirVisibility(od);
        
        // Ana satır
        if (visibility.ana) {
          allDataRows.push([rowIndex++, ...selectedColumns.map(col => {
            // Ana satırda Ek Taahhüt ve Ek Protokol tarih sütunları BOŞ olmalı
            if (col === 'ek_taahhut_talep_tarihi' || col === 'ek_protokol_talep_tarihi') return '-';
            if (col === 'talep_tutari') return displayMoney(od[col]);
            return String(od[col] || '-');
          })]);
        }
        
        // Ek Taahhütler - YENİ LİSTE YAPISI (Durum filtresine göre)
        if (visibility.ekTaahhut && od.ek_taahhutler && od.ek_taahhutler.length > 0) {
          od.ek_taahhutler.forEach((taahhut, tIndex) => {
            // Durum filtresi kontrolü - tablodaki gibi
            let includeTaahhut = false;
            if (filters.odenek_durumu.length === 0) {
              includeTaahhut = true;
            } else {
              includeTaahhut = filters.odenek_durumu.includes(taahhut.durumu);
            }
            
            if (!includeTaahhut) return; // Bu ek taahhütü atla
            
            const ekRow = [rowIndex++];
            selectedColumns.forEach(col => {
              if (col === 'yil') {
                const yil = taahhut.tarih ? taahhut.tarih.split('-')[0] : od.yil;
                ekRow.push(yil);
              }
              else if (col === 'isin_adi') ekRow.push(`  -> Ek Taahhüt #${tIndex + 1} (${od.isin_adi})`);
              else if (col === 'odenek_turu') ekRow.push('EK TAAHHUT');
              else if (col === 'talep_tutari') ekRow.push(displayMoney(taahhut.tutari));
              else if (col === 'odenek_durumu') ekRow.push((taahhut.durumu || ''.toUpperCase()));
              else if (col === 'talep_yazisi_sayi_no') ekRow.push(taahhut.yazisi || '-');
              else if (col === 'talep_tarihi' || col === 'ek_taahhut_talep_tarihi') ekRow.push(taahhut.tarih || '-');
              else if (col === 'talep_yeri') ekRow.push((taahhut.talep_yeri || od.talep_yeri || '-'.toUpperCase()));
              else ekRow.push('');
            });
            allDataRows.push(ekRow);
          });
        }
        // Fallback: Eski tek field (geriye uyumluluk)
        else if (visibility.ekTaahhut && od.ek_taahhut_talep_tutari) {
          const ekRow = [rowIndex++];
          selectedColumns.forEach(col => {
            if (col === 'yil') {
              const tarih = od.ek_taahhut_talep_tarihi;
              const yil = tarih ? tarih.split('-')[0] : '-';
              ekRow.push(yil);
            }
            else if (col === 'isin_adi') ekRow.push(`  -> Ek Taahhüt (${od.isin_adi})`);
            else if (col === 'odenek_turu') ekRow.push('EK TAAHHUT');
            else if (col === 'talep_tutari') ekRow.push(displayMoney(od.ek_taahhut_talep_tutari));
            else if (col === 'odenek_durumu') ekRow.push((od.ek_taahhut_odenek_durumu || ''.toUpperCase()));
            else if (col === 'talep_yazisi_sayi_no') ekRow.push(od.ek_taahhut_talep_yazisi || '-');
            else if (col === 'talep_tarihi' || col === 'ek_taahhut_talep_tarihi') ekRow.push(od.ek_taahhut_talep_tarihi || '-');
            else if (col === 'talep_yeri') ekRow.push((od.talep_yeri || '-'.toUpperCase()));
            else ekRow.push('');
          });
          allDataRows.push(ekRow);
        }
        
        // Ek Protokoller - YENİ LİSTE YAPISI (Durum filtresine göre)
        if (visibility.ekProtokol && od.ek_protokoller && od.ek_protokoller.length > 0) {
          od.ek_protokoller.forEach((protokol, pIndex) => {
            // Durum filtresi kontrolü - tablodaki gibi
            let includeProtokol = false;
            if (filters.odenek_durumu.length === 0) {
              includeProtokol = true;
            } else {
              includeProtokol = filters.odenek_durumu.includes(protokol.durumu);
            }
            
            if (!includeProtokol) return; // Bu ek protokolü atla
            
            const ekRow = [rowIndex++];
            selectedColumns.forEach(col => {
              if (col === 'yil') {
                const yil = protokol.tarih ? protokol.tarih.split('-')[0] : od.yil;
                ekRow.push(yil);
              }
              else if (col === 'isin_adi') ekRow.push(`  -> Ek Protokol #${pIndex + 1} (${od.isin_adi})`);
              else if (col === 'odenek_turu') ekRow.push('EK PROTOKOL');
              else if (col === 'talep_tutari') ekRow.push(displayMoney(protokol.tutari));
              else if (col === 'odenek_durumu') ekRow.push((protokol.durumu || ''.toUpperCase()));
              else if (col === 'talep_yazisi_sayi_no') ekRow.push(protokol.yazisi || '-');
              else if (col === 'talep_tarihi' || col === 'ek_protokol_talep_tarihi') ekRow.push(protokol.tarih || '-');
              else if (col === 'talep_yeri') ekRow.push((protokol.talep_yeri || od.talep_yeri || '-'.toUpperCase()));
              else ekRow.push('');
            });
            allDataRows.push(ekRow);
          });
        }
        // Fallback: Eski tek field (geriye uyumluluk)
        else if (visibility.ekProtokol && od.ek_protokol_talep_tutari) {
          const ekRow = [rowIndex++];
          selectedColumns.forEach(col => {
            if (col === 'yil') {
              // Ek Taahhüt tarihi varsa yılını çıkar
              const tarih = od.ek_taahhut_talep_tarihi;
              const yil = tarih ? tarih.split('-')[0] : '-';
              ekRow.push(yil);
            }
            else if (col === 'isin_adi') ekRow.push(`  -> Ek Taahhüt (${od.isin_adi})`);
            else if (col === 'odenek_turu') ekRow.push('EK TAAHHUT');
            else if (col === 'talep_tutari') ekRow.push(displayMoney(od.ek_taahhut_talep_tutari));
            else if (col === 'odenek_durumu') ekRow.push((od.ek_taahhut_odenek_durumu || ''.toUpperCase()));
            else if (col === 'talep_yazisi_sayi_no') ekRow.push(od.ek_taahhut_talep_yazisi || '-');
            else if (col === 'talep_tarihi' || col === 'ek_taahhut_talep_tarihi') ekRow.push(od.ek_taahhut_talep_tarihi || '-');
            else if (col === 'talep_yeri') ekRow.push((od.talep_yeri || '-'.toUpperCase()));
            else ekRow.push('');
          });
          allDataRows.push(ekRow);
        }
        
        // Fallback: Eski tek field (geriye uyumluluk)
        else if (visibility.ekProtokol && od.ek_protokol_talep_tutari) {
          const ekRow = [rowIndex++];
          selectedColumns.forEach(col => {
            if (col === 'yil') {
              const tarih = od.ek_protokol_talep_tarihi;
              const yil = tarih ? tarih.split('-')[0] : '-';
              ekRow.push(yil);
            }
            else if (col === 'isin_adi') ekRow.push(`  -> Ek Protokol (${od.isin_adi})`);
            else if (col === 'odenek_turu') ekRow.push('EK PROTOKOL');
            else if (col === 'talep_tutari') ekRow.push(displayMoney(od.ek_protokol_talep_tutari));
            else if (col === 'odenek_durumu') ekRow.push((od.ek_protokol_odenek_durumu || ''.toUpperCase()));
            else if (col === 'talep_yazisi_sayi_no') ekRow.push(od.ek_protokol_talep_yazisi || '-');
            else if (col === 'talep_tarihi' || col === 'ek_protokol_talep_tarihi') ekRow.push(od.ek_protokol_talep_tarihi || '-');
            else if (col === 'talep_yeri') ekRow.push((od.talep_yeri || '-'.toUpperCase()));
            else ekRow.push('');
          });
          allDataRows.push(ekRow);
        }
      });

      const parasalVar = selectedColumns.includes('talep_tutari');
      let allRows = allDataRows;
      
      if (parasalVar) {
        const toplamRow = ['TOPLAM'];
        selectedColumns.forEach(col => {
          if (col === 'talep_tutari') {
            let total = 0;
            dataToExport.forEach(o => {
              const visibility = getSatirVisibility(o);
              
              // Ana tutar
              if (visibility.ana) {
                const v1 = parseFloat((o.talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                if (!isNaN(v1)) total += v1;
              }
              
              // Ek Taahhütler - YENİ LİSTE YAPISI (Durum filtresine göre)
              if (visibility.ekTaahhut) {
                if (o.ek_taahhutler && o.ek_taahhutler.length > 0) {
                  o.ek_taahhutler.forEach(t => {
                    // Durum filtresi kontrolü - sadece eşleşenleri topla
                    let includeTaahhut = false;
                    if (filters.odenek_durumu.length === 0) {
                      includeTaahhut = true;
                    } else {
                      includeTaahhut = filters.odenek_durumu.includes(t.durumu);
                    }
                    
                    if (includeTaahhut) {
                      const val = parseFloat((t.tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                      if (!isNaN(val)) total += val;
                    }
                  });
                } else if (o.ek_taahhut_talep_tutari) {
                  const v2 = parseFloat((o.ek_taahhut_talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                  if (!isNaN(v2)) total += v2;
                }
              }
              
              // Ek Protokoller - YENİ LİSTE YAPISI (Durum filtresine göre)
              if (visibility.ekProtokol) {
                if (o.ek_protokoller && o.ek_protokoller.length > 0) {
                  o.ek_protokoller.forEach(p => {
                    // Durum filtresi kontrolü - sadece eşleşenleri topla
                    let includeProtokol = false;
                    if (filters.odenek_durumu.length === 0) {
                      includeProtokol = true;
                    } else {
                      includeProtokol = filters.odenek_durumu.includes(p.durumu);
                    }
                    
                    if (includeProtokol) {
                      const val = parseFloat((p.tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                      if (!isNaN(val)) total += val;
                    }
                  });
                } else if (o.ek_protokol_talep_tutari) {
                  const v3 = parseFloat((o.ek_protokol_talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                  if (!isNaN(v3)) total += v3;
                }
              }
            });
            toplamRow.push(total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL');
          } else {
            toplamRow.push('');
          }
        });
        allRows = [...allDataRows, toplamRow];
      }

      await exportTableToPDF(headers, allRows, 'Ödenek Listesi', `odenek${selectedRows.length > 0 ? '-secili' : ''}.pdf`, {
        subtitle: `Kayıt: ${dataToExport.length}${selectedRows.length > 0 ? ' (Seçili)' : ''}`,
        hasTotalRow: parasalVar,
        pageSize: pdfPageSize
      });

      setExportDialogOpen(false);
    } catch (error) {
      console.error('PDF error:', error);
      alert('PDF oluşturulurken hata: ' + error.message);
    }
  };

  const exportExcel = () => {
    if (selectedColumns.length === 0) { alert('Lütfen en az bir sütun seçin'); return; }

    let dataToExport;
    if (selectedRows.length > 0) {
      dataToExport = filteredOdenekler.filter(o => selectedRows.includes(o.id));
    } else {
      dataToExport = filteredOdenekler;
    }

    if (dataToExport.length === 0) { alert('Dışa aktarılacak ödenek yok'); return; }

    try {
      const columnLabels = {
        'yil': 'Yıl', 'isin_adi': 'İşin Adı', 'talep_yeri': 'Talep Yeri',
        'odenek_turu': 'Ödenek Türü', 'talep_tarihi': 'Talep Tarihi',
        'talep_yazisi_sayi_no': 'Talep Yazısı Sayı/No', 'talep_tutari': 'Talep Tutarı',
        'odenek_durumu': 'Ödenek Durumu',
        'ihale_onayi': 'İhale Onayı', 'ihale_onayi_talep_tarihi': 'İhale Onayı Talep Tarihi',
        'ihale_onayi_talep_yazisi': 'İhale Onayı Talep Yazısı',
        'ek_taahhut_talep_tarihi': 'Ek Taahhüt Tarihi',
        'ek_protokol_talep_tarihi': 'Ek Protokol Tarihi'
      };

      // Excel için satırlar oluştur (ana + alt satırlar)
      const excelData = [];
      let rowIndex = 1;
      
      dataToExport.forEach(od => {
        const visibility = getSatirVisibility(od);
        
        // Ana satır
        if (visibility.ana) {
          const row = { 'S.NO': rowIndex++ };
          selectedColumns.forEach(col => {
            // Ana satırda Ek Taahhüt ve Ek Protokol tarih sütunları BOŞ olmalı
            if (col === 'ek_taahhut_talep_tarihi' || col === 'ek_protokol_talep_tarihi') {
              row[columnLabels[col]] = '';
            }
            else if (col === 'talep_tutari') row[columnLabels[col]] = displayMoney(od[col]);
            else row[columnLabels[col] || col] = od[col] || '';
          });
          excelData.push(row);
        }
        
        // Ek Taahhüt satırları (yeni liste yapısı)
        if (visibility.ekTaahhut) {
          if (od.ek_taahhutler && od.ek_taahhutler.length > 0) {
            od.ek_taahhutler.forEach((taahhut, tIdx) => {
              // Durum filtresine göre kontrol
              if (filters.odenek_durumu.length > 0 && !filters.odenek_durumu.includes(taahhut.durumu)) return;
              
              const row = { 'S.NO': rowIndex++ };
              selectedColumns.forEach(col => {
                if (col === 'yil') {
                  const yil = taahhut.tarih ? taahhut.tarih.split('-')[0] : od.yil;
                  row[columnLabels[col]] = yil;
                }
                else if (col === 'isin_adi') row[columnLabels[col]] = `  → Ek Taahhüt #${tIdx + 1} (${od.isin_adi})`;
                else if (col === 'odenek_turu') row[columnLabels[col]] = 'EK TAAHHÜT';
                else if (col === 'talep_tutari') row[columnLabels[col]] = displayMoney(taahhut.tutari);
                else if (col === 'odenek_durumu') row[columnLabels[col]] = (taahhut.durumu || '').toUpperCase();
                else if (col === 'talep_yazisi_sayi_no') row[columnLabels[col]] = taahhut.yazisi || '';
                else if (col === 'talep_tarihi' || col === 'ek_taahhut_talep_tarihi') row[columnLabels[col]] = taahhut.tarih || '';
                else if (col === 'talep_yeri') row[columnLabels[col]] = (taahhut.talep_yeri || od.talep_yeri || '').toUpperCase();
                else row[columnLabels[col] || col] = '';
              });
              excelData.push(row);
            });
          } else if (od.ek_taahhut_talep_tutari) {
            // Eski tek-field fallback
            const row = { 'S.NO': rowIndex++ };
            selectedColumns.forEach(col => {
              if (col === 'yil') {
                const tarih = od.ek_taahhut_talep_tarihi;
                row[columnLabels[col]] = tarih ? tarih.split('-')[0] : '';
              }
              else if (col === 'isin_adi') row[columnLabels[col]] = `  → Ek Taahhüt (${od.isin_adi})`;
              else if (col === 'odenek_turu') row[columnLabels[col]] = 'EK TAAHHÜT';
              else if (col === 'talep_tutari') row[columnLabels[col]] = displayMoney(od.ek_taahhut_talep_tutari);
              else if (col === 'odenek_durumu') row[columnLabels[col]] = (od.ek_taahhut_odenek_durumu || '').toUpperCase();
              else if (col === 'talep_yazisi_sayi_no') row[columnLabels[col]] = od.ek_taahhut_talep_yazisi || '';
              else if (col === 'talep_tarihi' || col === 'ek_taahhut_talep_tarihi') row[columnLabels[col]] = od.ek_taahhut_talep_tarihi || '';
              else if (col === 'talep_yeri') row[columnLabels[col]] = (od.talep_yeri || '').toUpperCase();
              else row[columnLabels[col] || col] = '';
            });
            excelData.push(row);
          }
        }
        
        // Ek Protokol satırları (yeni liste yapısı)
        if (visibility.ekProtokol) {
          if (od.ek_protokoller && od.ek_protokoller.length > 0) {
            od.ek_protokoller.forEach((protokol, pIdx) => {
              // Durum filtresine göre kontrol
              if (filters.odenek_durumu.length > 0 && !filters.odenek_durumu.includes(protokol.durumu)) return;
              
              const row = { 'S.NO': rowIndex++ };
              selectedColumns.forEach(col => {
                if (col === 'yil') {
                  const yil = protokol.tarih ? protokol.tarih.split('-')[0] : od.yil;
                  row[columnLabels[col]] = yil;
                }
                else if (col === 'isin_adi') row[columnLabels[col]] = `  → Ek Protokol #${pIdx + 1} (${od.isin_adi})`;
                else if (col === 'odenek_turu') row[columnLabels[col]] = 'EK PROTOKOL';
                else if (col === 'talep_tutari') row[columnLabels[col]] = displayMoney(protokol.tutari);
                else if (col === 'odenek_durumu') row[columnLabels[col]] = (protokol.durumu || '').toUpperCase();
                else if (col === 'talep_yazisi_sayi_no') row[columnLabels[col]] = protokol.yazisi || '';
                else if (col === 'talep_tarihi' || col === 'ek_protokol_talep_tarihi') row[columnLabels[col]] = protokol.tarih || '';
                else if (col === 'talep_yeri') row[columnLabels[col]] = (protokol.talep_yeri || od.talep_yeri || '').toUpperCase();
                else row[columnLabels[col] || col] = '';
              });
              excelData.push(row);
            });
          } else if (od.ek_protokol_talep_tutari) {
            // Eski tek-field fallback
            const row = { 'S.NO': rowIndex++ };
            selectedColumns.forEach(col => {
              if (col === 'yil') {
                const tarih = od.ek_protokol_talep_tarihi;
                row[columnLabels[col]] = tarih ? tarih.split('-')[0] : '';
              }
              else if (col === 'isin_adi') row[columnLabels[col]] = `  → Ek Protokol (${od.isin_adi})`;
              else if (col === 'odenek_turu') row[columnLabels[col]] = 'EK PROTOKOL';
              else if (col === 'talep_tutari') row[columnLabels[col]] = displayMoney(od.ek_protokol_talep_tutari);
              else if (col === 'odenek_durumu') row[columnLabels[col]] = (od.ek_protokol_odenek_durumu || '').toUpperCase();
              else if (col === 'talep_yazisi_sayi_no') row[columnLabels[col]] = od.ek_protokol_talep_yazisi || '';
              else if (col === 'talep_tarihi' || col === 'ek_protokol_talep_tarihi') row[columnLabels[col]] = od.ek_protokol_talep_tarihi || '';
              else if (col === 'talep_yeri') row[columnLabels[col]] = (od.talep_yeri || '').toUpperCase();
              else row[columnLabels[col] || col] = '';
            });
            excelData.push(row);
          }
        }
      });

      if (selectedColumns.includes('talep_tutari')) {
        const totalsRow = { 'S.NO': 'TOPLAM' };
        selectedColumns.forEach(col => {
          if (col === 'talep_tutari') {
            let total = 0;
            dataToExport.forEach(o => {
              const visibility = getSatirVisibility(o);
              
              // Sadece görünen satırların tutarlarını topla
              if (visibility.ana) {
                const v1 = parseFloat((o.talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                if (!isNaN(v1)) total += v1;
              }
              if (visibility.ekTaahhut) {
                if (o.ek_taahhutler && o.ek_taahhutler.length > 0) {
                  o.ek_taahhutler.forEach(t => {
                    if (filters.odenek_durumu.length > 0 && !filters.odenek_durumu.includes(t.durumu)) return;
                    const val = parseFloat((t.tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                    if (!isNaN(val)) total += val;
                  });
                } else if (o.ek_taahhut_talep_tutari) {
                  const v2 = parseFloat((o.ek_taahhut_talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                  if (!isNaN(v2)) total += v2;
                }
              }
              if (visibility.ekProtokol) {
                if (o.ek_protokoller && o.ek_protokoller.length > 0) {
                  o.ek_protokoller.forEach(p => {
                    if (filters.odenek_durumu.length > 0 && !filters.odenek_durumu.includes(p.durumu)) return;
                    const val = parseFloat((p.tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                    if (!isNaN(val)) total += val;
                  });
                } else if (o.ek_protokol_talep_tutari) {
                  const v3 = parseFloat((o.ek_protokol_talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                  if (!isNaN(v3)) total += v3;
                }
              }
            });
            totalsRow[columnLabels[col]] = total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL';
          } else {
            totalsRow[columnLabels[col] || col] = '';
          }
        });
        excelData.push(totalsRow);
      }

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [{ wch: 8 }, { wch: 15 }, { wch: 40 }, { wch: 15 }, { wch: 20 }, { wch: 25 }, { wch: 18 }, { wch: 18 }];
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ödenekler');
      XLSX.writeFile(workbook, `odenek${selectedRows.length > 0 ? '-secili' : ''}.xlsx`);
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Excel error:', error);
      alert('Excel oluşturulurken hata: ' + error.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  const filteredOdenekler = odenekler.filter(o => {
    if (filters.isin_adi && !o.isin_adi.toLowerCase().includes(filters.isin_adi.toLowerCase())) return false;
    if (filters.yil.length > 0 && !filters.yil.includes(o.yil?.toString())) return false;
    if (filters.talep_yeri.length > 0 && !filters.talep_yeri.includes(o.talep_yeri)) return false;
    
    // Ödenek türü filtresi: Ana kayıt veya alt satırlar eşleşmeli
    if (filters.odenek_turu.length > 0) {
      const anaMatch = filters.odenek_turu.includes(o.odenek_turu);
      const ekTaahhutMatch = filters.odenek_turu.includes('ek_taahhut') && 
        ((o.ek_taahhutler && o.ek_taahhutler.length > 0) || !!o.ek_taahhut_talep_tutari);
      const ekProtokolMatch = filters.odenek_turu.includes('ek_protokol') && 
        ((o.ek_protokoller && o.ek_protokoller.length > 0) || !!o.ek_protokol_talep_tutari);
      if (!anaMatch && !ekTaahhutMatch && !ekProtokolMatch) return false;
      
      // Eğer ödenek türü seçili ve durum da seçili ise, o türün durumunu kontrol et
      if (filters.odenek_durumu.length > 0) {
        if (filters.odenek_turu.includes('ek_taahhut')) {
          const newArrayMatch = o.ek_taahhutler && o.ek_taahhutler.some(t => filters.odenek_durumu.includes(t.durumu));
          return newArrayMatch;
        } else if (filters.odenek_turu.includes('ek_protokol')) {
          const newArrayMatch = o.ek_protokoller && o.ek_protokoller.some(p => filters.odenek_durumu.includes(p.durumu));
          return newArrayMatch;
        } else {
          return filters.odenek_durumu.includes(o.odenek_durumu);
        }
      }
    } else {
      // Ödenek türü filtresi YOK (all) - Sadece durum filtresine bak
      if (filters.odenek_durumu.length > 0) {
        const anaMatch = filters.odenek_durumu.includes(o.odenek_durumu);
        const ekTaahhutMatch = (o.ek_taahhutler && o.ek_taahhutler.some(t => filters.odenek_durumu.includes(t.durumu))) ||
                              (o.ek_taahhut_odenek_durumu && filters.odenek_durumu.includes(o.ek_taahhut_odenek_durumu));
        const ekProtokolMatch = (o.ek_protokoller && o.ek_protokoller.some(p => filters.odenek_durumu.includes(p.durumu))) ||
                                (o.ek_protokol_odenek_durumu && filters.odenek_durumu.includes(o.ek_protokol_odenek_durumu));
        if (!anaMatch && !ekTaahhutMatch && !ekProtokolMatch) return false;
      }
    }
    return true;
  });

  // Her ödenek için hangi satırların gösterileceğini belirle
  const getSatirVisibility = (odenek) => {
    const hasEkTaahhut = (odenek.ek_taahhutler && odenek.ek_taahhutler.length > 0) || !!odenek.ek_taahhut_talep_tutari;
    const hasEkProtokol = (odenek.ek_protokoller && odenek.ek_protokoller.length > 0) || !!odenek.ek_protokol_talep_tutari;
    
    // Ödenek türü filtresine göre
    if (filters.odenek_turu.length > 0) {
      const showAna = filters.odenek_turu.includes('taahhut') || filters.odenek_turu.includes('protokol');
      const showEkTaahhut = filters.odenek_turu.includes('ek_taahhut') && hasEkTaahhut;
      const showEkProtokol = filters.odenek_turu.includes('ek_protokol') && hasEkProtokol;
      
      if (filters.odenek_durumu.length === 0) {
        return { ana: showAna, ekTaahhut: showEkTaahhut, ekProtokol: showEkProtokol };
      }
      
      // Tür + durum birlikte seçili
      return {
        ana: showAna && filters.odenek_durumu.includes(odenek.odenek_durumu),
        ekTaahhut: showEkTaahhut,
        ekProtokol: showEkProtokol
      };
    }
    
    // Ödenek türü seçili değil
    if (filters.odenek_durumu.length === 0) {
      return { ana: true, ekTaahhut: hasEkTaahhut, ekProtokol: hasEkProtokol };
    }
    
    // Sadece durum filtresi aktif - her satırın kendi durumunu kontrol et
    const anaMatch = filters.odenek_durumu.includes(odenek.odenek_durumu);
    
    let ekTaahhutMatch = false;
    if (odenek.ek_taahhutler && odenek.ek_taahhutler.length > 0) {
      ekTaahhutMatch = odenek.ek_taahhutler.some(t => filters.odenek_durumu.includes(t.durumu));
    } else if (odenek.ek_taahhut_odenek_durumu && filters.odenek_durumu.includes(odenek.ek_taahhut_odenek_durumu)) {
      ekTaahhutMatch = true;
    }
    
    let ekProtokolMatch = false;
    if (odenek.ek_protokoller && odenek.ek_protokoller.length > 0) {
      ekProtokolMatch = odenek.ek_protokoller.some(p => filters.odenek_durumu.includes(p.durumu));
    } else if (odenek.ek_protokol_odenek_durumu && filters.odenek_durumu.includes(odenek.ek_protokol_odenek_durumu)) {
      ekProtokolMatch = true;
    }
    
    return {
      ana: anaMatch,
      ekTaahhut: ekTaahhutMatch && hasEkTaahhut,
      ekProtokol: ekProtokolMatch && hasEkProtokol
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Ödenek Modülü</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-gray-300 hover:text-white" data-testid="anasayfa-nav-button">Anasayfa</Button>
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-300 hover:text-white">Dashboard</Button>
              <Button onClick={() => navigate('/finance/ihaleler')} variant="ghost" className="text-gray-300 hover:text-white">İhaleler</Button>
              <Button onClick={() => navigate('/finance/hakedis')} variant="ghost" className="text-gray-300 hover:text-white">Hakediş</Button>
              <Button onClick={() => navigate('/finance/santiye-takip')} variant="ghost" className="text-gray-300 hover:text-white">Şantiye Takip</Button>
              <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300">Çıkış</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">💰 Ödenek/Taahhüt</h2>
            <p className="text-gray-400">Ödenek ve taahhüt takip sistemi</p>
          </div>
          <div className="flex gap-3">
            {odenekler.length > 0 && (
              <Button onClick={() => {
                setExportDialogOpen(true);
                if (selectedColumns.length === 0) {
                  setSelectedColumns(['yil', 'isin_adi', 'talep_yeri', 'odenek_durumu', 'talep_tutari']);
                }
              }} className="bg-blue-600 hover:bg-blue-700 text-white">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                PDF/Excel İndir
              </Button>
            )}
            {!user?.is_viewer && (
              <Button onClick={openCreateDialog} className="bg-green-600 hover:bg-green-700 text-white">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Ödenek/Taahhüt Ekle
              </Button>
            )}
          </div>
        </div>

        {/* Filtreler */}
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          totalCount={odenekler.length}
          filteredCount={filteredOdenekler.length}
          resetFilters={() => setFilters({ isin_adi: '', yil: [], talep_yeri: [], odenek_turu: [], odenek_durumu: [] })}
          filterConfig={[
            { key: 'isin_adi', label: 'İş Adı', type: 'text' },
            { key: 'yil', label: 'Yıl', type: 'multi', options: [...new Set(odenekler.map(o => o.yil?.toString()))].filter(Boolean).sort((a,b) => b-a).map(y => ({v:y,l:y})) },
            { key: 'talep_yeri', label: 'Talep Yeri', type: 'multi', options: [{v:'yigm',l:'YİGM'},{v:'spor_toto',l:'SPOR TOTO'},{v:'strateji',l:'STRATEJİ'}] },
            { key: 'odenek_turu', label: 'Ödenek Türü', type: 'multi', options: [{v:'taahhut',l:'TAAHHÜT'},{v:'ek_taahhut',l:'EK TAAHHÜT'},{v:'protokol',l:'PROTOKOL'},{v:'ek_protokol',l:'EK PROTOKOL'}] },
            { key: 'odenek_durumu', label: 'Ödenek Durumu', type: 'multi', options: [{v:'geldi',l:'GELDİ'},{v:'gelmedi',l:'GELMEDİ'},{v:'iptal_edildi',l:'İPTAL EDİLDİ'}] },
          ]}
        />

        {filteredOdenekler.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400">Henüz ödenek eklenmemiş</p>
            </CardContent>
          </Card>
        ) : (
          <>
          <div className="flex justify-end mb-3">
            <SortSelect value={sortKey} onChange={setSortKey} options={['newest','oldest','updated','name_asc','name_desc','amount_high','amount_low']} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 border-b border-gray-800">
                <tr>
                  <th className="px-4 py-3 text-center">
                    <input type="checkbox" checked={selectedRows.length === filteredOdenekler.length && filteredOdenekler.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) { setSelectedRows(filteredOdenekler.map(o => o.id)); }
                        else { setSelectedRows([]); }
                      }} className="w-4 h-4" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">S.NO</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Yıl</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">İşin Adı</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Talep Yeri</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Ödenek Türü</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400">Talep Tutarı</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Durum</th>
                  {user?.is_admin && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Son Değiştiren</th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {sortData(filteredOdenekler, sortKey, 'isin_adi', 'talep_tutari').map((od, index) => {
                  const visibility = getSatirVisibility(od);
                  return (
                  <React.Fragment key={od.id}>
                    {visibility.ana && (
                    <tr className={`${selectedRows.includes(od.id) ? 'bg-blue-900/30' : 'bg-gray-900/50'} hover:bg-gray-900 cursor-pointer`} data-testid={`odenek-row-${od.id}`}
                      onClick={(e) => { if (e.target.closest('button, input, a, [role="checkbox"]')) return; setDetailOdenek(od); }}>
                    <td className="px-4 py-3 text-center">
                      <input type="checkbox" checked={selectedRows.includes(od.id)}
                        onChange={(e) => {
                          if (e.target.checked) { setSelectedRows([...selectedRows, od.id]); }
                          else { setSelectedRows(selectedRows.filter(id => id !== od.id)); }
                        }} className="w-4 h-4" />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">{index + 1}</td>
                    <td className="px-4 py-3 text-sm text-white">{od.yil}</td>
                    <td className="px-4 py-3 text-sm text-gray-300 max-w-md truncate">{od.isin_adi}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{od.talep_yeri.toUpperCase()}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{od.odenek_turu.toUpperCase()}</td>
                    <td className="px-4 py-3 text-sm text-gray-300 text-right">
                      <div className="text-white">{displayMoney(od.talep_tutari)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">{od.odenek_durumu.toUpperCase()}</Badge>
                    </td>
                    {user?.is_admin && (
                      <td className="px-4 py-3 text-xs text-gray-400">
                        <div>{od.last_modified_by_name || '-'}</div>
                        {od.last_modified_at && (
                          <div className="text-gray-500 text-xs mt-1">
                            {new Date(od.last_modified_at).toLocaleString('tr-TR')}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        {!user?.is_viewer && (
                          <Button onClick={() => openEditDialog(od)} size="sm" variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">Düzenle</Button>
                        )}
                        {user?.is_admin && (
                          <Button onClick={() => handleDelete(od.id)} size="sm" variant="outline" className="bg-red-900/20 border-red-700 text-red-400 hover:bg-red-900/40">Sil</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  )}
                  
                  {/* Ek Taahhüt Alt Satırlar - MULTIPLE ROWS */}
                  {od.ek_taahhutler && od.ek_taahhutler.length > 0 && od.ek_taahhutler.map((taahhut, tIndex) => {
                    // Her ek taahhüt için gösterilip gösterilmeyeceğini kontrol et
                    let showThisRow = false;
                    
                    // Tür filtresi kontrolü
                    if (filters.odenek_turu.length === 0 || filters.odenek_turu.includes('ek_taahhut')) {
                      // Durum filtresi kontrolü
                      if (filters.odenek_durumu.length === 0) {
                        showThisRow = true; // Tüm durumlar - hepsini göster
                      } else {
                        showThisRow = filters.odenek_durumu.includes(taahhut.durumu); // Sadece eşleşen durumu göster
                      }
                    }
                    
                    if (!showThisRow) return null;
                    
                    return (
                      <tr key={`taahhut-${od.id}-${tIndex}`} className="bg-yellow-900/10 cursor-pointer hover:bg-yellow-900/20" data-testid={`odenek-ek-taahhut-row-${od.id}-${tIndex}`}
                        onClick={(e) => { if (e.target.closest('button, input, a')) return; setDetailOdenek(od); }}>
                        <td className="px-4 py-2 text-center">
                          <input type="checkbox" checked={selectedRows.includes(od.id)} 
                            onChange={(e) => {
                              if (e.target.checked) setSelectedRows([...selectedRows, od.id]);
                              else setSelectedRows(selectedRows.filter(id => id !== od.id));
                            }} className="w-4 h-4" />
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">({index + 1}.{tIndex + 1})</td>
                        <td className="px-4 py-2 text-xs">{od.yil}</td>
                        <td className="px-4 py-2 text-sm text-gray-300">
                          <div className="text-yellow-400 text-xs mb-1">↳ Ek Taahhüt #{tIndex + 1}</div>
                          <div className="text-gray-400 text-xs truncate max-w-md">{od.isin_adi}</div>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-400">{(taahhut.talep_yeri || od.talep_yeri).toUpperCase()}</td>
                        <td className="px-4 py-2 text-xs text-gray-400">EK TAAHHÜT</td>
                        <td className="px-4 py-2 text-xs text-yellow-300 text-right">{displayMoney(taahhut.tutari)}</td>
                        <td className="px-4 py-2 text-xs"><Badge className="text-xs">{(taahhut.durumu || '').toUpperCase()}</Badge></td>
                        {user?.is_admin && <td className="px-4 py-2 text-xs text-gray-500">-</td>}
                        <td></td>
                      </tr>
                    );
                  }).filter(row => row !== null)}
                  
                  {/* Fallback: Eski tek field (geriye uyumluluk) */}
                  {visibility.ekTaahhut && (!od.ek_taahhutler || od.ek_taahhutler.length === 0) && od.ek_taahhut_talep_tutari && (
                    <tr className="bg-yellow-900/10">
                      <td className="px-4 py-2 text-center">
                        <input type="checkbox" checked={selectedRows.includes(od.id)} 
                          onChange={(e) => {
                            if (e.target.checked) setSelectedRows([...selectedRows, od.id]);
                            else setSelectedRows(selectedRows.filter(id => id !== od.id));
                          }} className="w-4 h-4" />
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">({index + 1})</td>
                      <td className="px-4 py-2 text-xs">{od.yil}</td>
                      <td className="px-4 py-2 text-sm text-gray-300">
                        <div className="text-yellow-400 text-xs mb-1">↳ Ek Taahhüt</div>
                        <div className="text-gray-400 text-xs truncate max-w-md">{od.isin_adi}</div>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-400">{od.talep_yeri.toUpperCase()}</td>
                      <td className="px-4 py-2 text-xs text-gray-400">EK TAAHHÜT</td>
                      <td className="px-4 py-2 text-xs text-gray-300 text-right">{displayMoney(od.ek_taahhut_talep_tutari)}</td>
                      <td className="px-4 py-2 text-xs"><Badge className="text-xs">{(od.ek_taahhut_odenek_durumu || '').toUpperCase()}</Badge></td>
                      {user?.is_admin && <td className="px-4 py-2 text-xs text-gray-500">-</td>}
                      <td></td>
                    </tr>
                  )}
                  
                  {/* Ek Protokol Alt Satırlar - MULTIPLE ROWS */}
                  {od.ek_protokoller && od.ek_protokoller.length > 0 && od.ek_protokoller.map((protokol, pIndex) => {
                    // Her ek protokol için gösterilip gösterilmeyeceğini kontrol et
                    let showThisRow = false;
                    
                    // Tür filtresi kontrolü
                    if (filters.odenek_turu.length === 0 || filters.odenek_turu.includes('ek_protokol')) {
                      // Durum filtresi kontrolü
                      if (filters.odenek_durumu.length === 0) {
                        showThisRow = true; // Tüm durumlar - hepsini göster
                      } else {
                        showThisRow = filters.odenek_durumu.includes(protokol.durumu); // Sadece eşleşen durumu göster
                      }
                    }
                    
                    if (!showThisRow) return null;
                    
                    return (
                      <tr key={`protokol-${od.id}-${pIndex}`} className="bg-purple-900/10 cursor-pointer hover:bg-purple-900/20" data-testid={`odenek-ek-protokol-row-${od.id}-${pIndex}`}
                        onClick={(e) => { if (e.target.closest('button, input, a')) return; setDetailOdenek(od); }}>
                        <td className="px-4 py-2 text-center">
                          <input type="checkbox" checked={selectedRows.includes(od.id)} 
                            onChange={(e) => {
                              if (e.target.checked) setSelectedRows([...selectedRows, od.id]);
                              else setSelectedRows(selectedRows.filter(id => id !== od.id));
                            }} className="w-4 h-4" />
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">({index + 1}.{pIndex + 1})</td>
                        <td className="px-4 py-2 text-xs">{od.yil}</td>
                        <td className="px-4 py-2 text-sm text-gray-300">
                          <div className="text-purple-400 text-xs mb-1">↳ Ek Protokol #{pIndex + 1}</div>
                          <div className="text-gray-400 text-xs truncate max-w-md">{od.isin_adi}</div>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-400">{(protokol.talep_yeri || od.talep_yeri).toUpperCase()}</td>
                        <td className="px-4 py-2 text-xs text-gray-400">EK PROTOKOL</td>
                        <td className="px-4 py-2 text-xs text-purple-300 text-right">{displayMoney(protokol.tutari)}</td>
                        <td className="px-4 py-2 text-xs"><Badge className="text-xs">{(protokol.durumu || '').toUpperCase()}</Badge></td>
                        {user?.is_admin && <td className="px-4 py-2 text-xs text-gray-500">-</td>}
                        <td></td>
                      </tr>
                    );
                  }).filter(row => row !== null)}
                  
                  {/* Fallback: Eski tek field (geriye uyumluluk) */}
                  {visibility.ekProtokol && (!od.ek_protokoller || od.ek_protokoller.length === 0) && od.ek_protokol_talep_tutari && (
                    <tr className="bg-purple-900/10">
                      <td className="px-4 py-2 text-center">
                        <input type="checkbox" checked={selectedRows.includes(od.id)} 
                          onChange={(e) => {
                            if (e.target.checked) setSelectedRows([...selectedRows, od.id]);
                            else setSelectedRows(selectedRows.filter(id => id !== od.id));
                          }} className="w-4 h-4" />
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">({index + 1})</td>
                      <td className="px-4 py-2 text-xs">{od.yil}</td>
                      <td className="px-4 py-2 text-sm text-gray-300">
                        <div className="text-purple-400 text-xs mb-1">↳ Ek Protokol</div>
                        <div className="text-gray-400 text-xs truncate max-w-md">{od.isin_adi}</div>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-400">{od.talep_yeri.toUpperCase()}</td>
                      <td className="px-4 py-2 text-xs text-gray-400">EK PROTOKOL</td>
                      <td className="px-4 py-2 text-xs text-gray-300 text-right">{displayMoney(od.ek_protokol_talep_tutari)}</td>
                      <td className="px-4 py-2 text-xs"><Badge className="text-xs">{(od.ek_protokol_odenek_durumu || '').toUpperCase()}</Badge></td>
                      {user?.is_admin && <td className="px-4 py-2 text-xs text-gray-500">-</td>}
                      <td></td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
                {/* TOPLAM SATIRI */}
                <tr className="bg-blue-900/20 border-t-2 border-blue-700">
                  <td colSpan={user?.is_admin ? "7" : "6"} className="px-4 py-3 text-sm font-bold text-blue-300 text-right">TOPLAM:</td>
                  <td className="px-4 py-3 text-sm font-bold text-blue-300 text-right">
                    {(() => {
                      let total = 0;
                      filteredOdenekler.forEach(o => {
                        const visibility = getSatirVisibility(o);
                        
                        // Sadece görünen satırların tutarlarını topla
                        if (visibility.ana) {
                          const val1 = parseFloat((o.talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                          if (!isNaN(val1)) total += val1;
                        }
                        if (visibility.ekTaahhut) {
                          // Yeni list yapısı
                          if (o.ek_taahhutler && o.ek_taahhutler.length > 0) {
                            o.ek_taahhutler.forEach(t => {
                              const val = parseFloat((t.tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                              if (!isNaN(val)) total += val;
                            });
                          } else {
                            // Eski tek field (geriye uyumluluk)
                            const val2 = parseFloat((o.ek_taahhut_talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                            if (!isNaN(val2)) total += val2;
                          }
                        }
                        if (visibility.ekProtokol) {
                          // Yeni list yapısı
                          if (o.ek_protokoller && o.ek_protokoller.length > 0) {
                            o.ek_protokoller.forEach(p => {
                              const val = parseFloat((p.tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                              if (!isNaN(val)) total += val;
                            });
                          } else {
                            // Eski tek field (geriye uyumluluk)
                            const val3 = parseFloat((o.ek_protokol_talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                            if (!isNaN(val3)) total += val3;
                          }
                        }
                      });
                      return total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL';
                    })()}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Seçili Toplamlar */}
        {selectedRows.length > 0 && (
          <Card className="mt-6 bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-700/50">
            <CardHeader>
              <CardTitle className="text-blue-400">📊 Seçili Ödenekler Toplamı ({selectedRows.length} ödenek)</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <p className="text-xs text-gray-400 mb-1">Talep Tutarı Toplamı:</p>
                <p className="text-2xl font-bold text-blue-300">
                  {(() => {
                    let total = 0;
                    odenekler.filter(o => selectedRows.includes(o.id)).forEach(o => {
                      const visibility = getSatirVisibility(o);
                      
                      // Sadece görünen satırların tutarlarını topla
                      if (visibility.ana) {
                        const v1 = parseFloat((o.talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                        if (!isNaN(v1)) total += v1;
                      }
                      if (visibility.ekTaahhut) {
                        const v2 = parseFloat((o.ek_taahhut_talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                        if (!isNaN(v2)) total += v2;
                      }
                      if (visibility.ekProtokol) {
                        const v3 = parseFloat((o.ek_protokol_talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(/\./g, '').replace(',', '.'));
                        if (!isNaN(v3)) total += v3;
                      }
                    });
                    return total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL';
                  })()}
                </p>
              </div>
              <div className="mt-4 flex gap-3">
                <Button onClick={() => {
                  setExportDialogOpen(true);
                  if (selectedColumns.length === 0) {
                    setSelectedColumns(['yil', 'isin_adi', 'talep_yeri', 'odenek_durumu', 'talep_tutari']);
                  }
                }} className="bg-purple-600 hover:bg-purple-700 text-white">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Seçilileri İndir (PDF/Excel)
                </Button>
                <Button onClick={() => setSelectedRows([])} variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">Seçimi Temizle</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <FinansOdenekDetailDialog
        open={!!detailOdenek}
        onOpenChange={(v) => { if (!v) setDetailOdenek(null); }}
        odenek={detailOdenek}
        hakedisler={hakedisler}
        displayMoney={displayMoney}
        canEdit={!user?.is_viewer}
        onEdit={(od) => openEditDialog(od)}
        onExportPDF={(od) => exportFinansOdenekDetayPDF(od, { displayMoney, hakedisler })}
        onOpenHakedis={() => navigate('/finance/hakedis')}
      />

      <OdenekFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editMode={editMode}
        formData={formData}
        setFormData={setFormData}
        displayMoney={displayMoney}
        onSubmit={handleSubmit}
        submitting={submitting}
        onCancel={() => { setDialogOpen(false); resetForm(); }}
        addEkTaahhut={addEkTaahhut}
        removeEkTaahhut={removeEkTaahhut}
        updateEkTaahhut={updateEkTaahhut}
        addEkProtokol={addEkProtokol}
        removeEkProtokol={removeEkProtokol}
        updateEkProtokol={updateEkProtokol}
      />

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        columns={[
          {id: 'yil', label: 'Yıl'},
          {id: 'isin_adi', label: 'İşin Adı'},
          {id: 'talep_yeri', label: 'Talep Yeri'},
          {id: 'odenek_turu', label: 'Ödenek Türü'},
          {id: 'talep_tarihi', label: 'Talep Tarihi'},
          {id: 'talep_yazisi_sayi_no', label: 'Talep Yazısı Sayı/No'},
          {id: 'talep_tutari', label: 'Talep Tutarı'},
          {id: 'odenek_durumu', label: 'Ödenek Durumu'},
          {id: 'ihale_onayi', label: 'İhale Onayı'},
          {id: 'ihale_onayi_talep_tarihi', label: 'İhale Onayı Talep Tarihi'},
          {id: 'ihale_onayi_talep_yazisi', label: 'İhale Onayı Talep Yazısı'},
          {id: 'ek_taahhut_talep_tarihi', label: 'Ek Taahhüt Tarihi'},
          {id: 'ek_protokol_talep_tarihi', label: 'Ek Protokol Tarihi'}
        ]}
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
        onSelectAll={() => selectAll([
          {id: 'yil'}, {id: 'isin_adi'}, {id: 'talep_yeri'}, {id: 'odenek_turu'},
          {id: 'talep_tarihi'}, {id: 'talep_yazisi_sayi_no'}, {id: 'talep_tutari'}, {id: 'odenek_durumu'},
          {id: 'ihale_onayi'}, {id: 'ihale_onayi_talep_tarihi'}, {id: 'ihale_onayi_talep_yazisi'},
          {id: 'ek_taahhut_talep_tarihi'}, {id: 'ek_protokol_talep_tarihi'}
        ])}
        onClearAll={clearAll}
        pdfPageSize={pdfPageSize}
        onPdfPageSizeChange={setPdfPageSize}
        onExportPDF={exportPDF}
        onExportExcel={exportExcel}
        gridCols={2}
        idPrefix="odenek-export"
      />
    </div>
  );
};

export default OdenekPage;